// ─── What: the conviction panel ───────────────────────────────────────────────
//
// A signed metric over time, one line per trader group, with the group's signal
// markers pinned to the line. Only exists when conviction data has points.
//
// The panel drives the shared crosshair, and its markers suppress it — that is
// why `chrome` is a declared dependency rather than closure scope.

import * as d3 from 'd3';
import { alpha } from '@mui/material/styles';
import type { SignalItem } from '../../../../api/types';
import type {
    ConvictionSeries,
    ConvictionTimePoint,
} from '../../../../api/useConvictionTimeseries';
import { drawPanelSeparator, drawPercentAxis } from '../../chartAxis';
import {
    placeTooltip,
    buildConvictionTooltipHtml,
    buildMarkerTooltipHtml,
} from '../../chartTooltips';
import { markerPath } from '../../svgPaths';
import type { PreparedSignal } from '../../chartTypes';
import { MARGIN } from '../layout';
import { CONV_GROUP_COLORS, type ConvictionMetric } from '../constants';
import { drawPanelToggle } from './panelChrome';
import type { ChartChrome, DrawCtx } from '../drawContext';

export interface ConvictionPanelDeps {
    convictionByGroup: Map<string, ConvictionSeries>;
    allConvictionPoints: ConvictionTimePoint[];
    convictionMetric: ConvictionMetric;
    convictionRawMode: boolean;
    onConvictionRawModeChange?: (raw: boolean) => void;
    traderSignals?: SignalItem[];
    onTraderHover?: (address: string | null) => void;
    eventEndDate?: Date | null;
    chrome: ChartChrome;
    onToggle: () => void;
}

/** Compact metric tick: money for net_capital, bare counts otherwise. */
function metricTickLabel(d: number, metric: ConvictionMetric): string {
    const abs = Math.abs(d);
    const sign = d < 0 ? '-' : '';
    const unit = metric === 'net_capital' ? '$' : '';
    if (abs >= 1e6) return `${sign}${unit}${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}${unit}${(abs / 1e3).toFixed(1)}K`;
    return `${sign}${unit}${abs.toFixed(0)}`;
}

/**
 * The conviction marker tooltip wants the same rich card the probability signal
 * pins use, so a raw SignalItem is widened to a PreparedSignal at hover time.
 * Deliberately NOT shared with overlays/prepareSignals: that one folds in
 * session.opening_fact fallbacks and clamps the score, neither of which applies
 * here.
 */
function preparedFromTraderSignal(
    d: SignalItem,
    px: number,
    py: number
): PreparedSignal {
    const fact = (d.fact ?? {}) as Record<string, unknown>;
    const session = (d.session ?? {}) as Record<string, unknown>;
    return {
        id: d.id,
        conditionId: d.market_id,
        openedAt: new Date(d.opened_at),
        closedAt: d.closed_at ? new Date(d.closed_at) : null,
        lastSeenAt: d.last_seen_at ? new Date(d.last_seen_at) : null,
        direction: d.direction,
        signalType: d.signal_type,
        traderGroup: d.trader_group,
        status: d.status,
        score: d.score,
        color: CONV_GROUP_COLORS[d.trader_group] ?? '#888',
        px,
        py,
        address: d.address ?? null,
        addressAlias: d.address_alias ?? null,
        factDeltaUsd: (fact.delta_usd as number) ?? null,
        factSide: (fact.side as string) ?? null,
        sessionOpeningPrice: (session.opening_price as number) ?? null,
        sessionPeakPrice: (session.peak_price as number) ?? null,
        sessionClosePrice: (session.close_price as number) ?? null,
        windowMinutes:
            d.signal_type === 'volume_anomaly'
                ? (fact.window_minutes as number) ?? null
                : null,
    };
}

export function drawConvictionPanel(
    ctx: DrawCtx,
    deps: ConvictionPanelDeps
): void {
    const { svg, layout, colors, theme, xScale, xDomain, now, questionMap, tooltipRef, svgEl } =
        ctx;
    const {
        convictionByGroup,
        allConvictionPoints,
        convictionMetric,
        convictionRawMode,
        onConvictionRawModeChange,
        traderSignals,
        onTraderHover,
        eventEndDate,
        chrome,
    } = deps;
    const { w, convTop, convBottom, convHeight, convVisible } = layout;
    const { isDark, gridColor, textColor, tc } = colors;

    drawPanelSeparator(
        svg,
        MARGIN.left,
        MARGIN.left + w,
        convTop - 1,
        gridColor
    );

    drawPanelToggle(svg, {
        top: convTop,
        centreX: MARGIN.left + w / 2,
        name: 'CONVICTION',
        open: convVisible,
        textColor,
        onToggle: deps.onToggle,
    });

    // ── RAW / LOCF pill ───────────────────────────────────────────────────
    if (convVisible && onConvictionRawModeChange) {
        const pillLabel = convictionRawMode ? 'LOCF' : 'RAW';
        const pillColor = convictionRawMode
            ? isDark
                ? '#f59e0b'
                : '#b45309'
            : isDark
              ? 'rgba(255,255,255,0.38)'
              : 'rgba(0,0,0,0.35)';
        const pillFill = convictionRawMode
            ? isDark
                ? 'rgba(245,158,11,0.15)'
                : 'rgba(180,83,9,0.10)'
            : isDark
              ? 'rgba(255,255,255,0.06)'
              : 'rgba(0,0,0,0.05)';

        const pillG = svg
            .append('g')
            .style('cursor', 'pointer')
            .on('click', () => onConvictionRawModeChange(!convictionRawMode));

        pillG
            .append('rect')
            .attr('x', MARGIN.left + w - 32)
            .attr('y', convTop - 13)
            .attr('width', 30)
            .attr('height', 11)
            .attr('rx', 3)
            .attr('fill', pillFill)
            .attr('stroke', pillColor)
            .attr('stroke-width', 0.5);

        pillG
            .append('text')
            .attr('x', MARGIN.left + w - 17)
            .attr('y', convTop - 5)
            .attr('fill', pillColor)
            .attr('font-size', '7px')
            .attr('font-family', 'monospace')
            .attr('font-weight', 700)
            .attr('text-anchor', 'middle')
            .text(pillLabel);
    }

    if (!convVisible) return;

    // ── Metric accessor ───────────────────────────────────────────────────
    const getValue = (p: ConvictionTimePoint): number => {
        switch (convictionMetric) {
            case 'net_capital':
                return p.net_capital;
            case 'net_traders':
                return p.net_traders;
            case 'raw_implied':
                return p.raw_implied;
            case 'open_shares_net':
                return p.open_shares_yes - p.open_shares_no;
        }
    };

    // ── Scale ─────────────────────────────────────────────────────────────
    let yMetric: d3.ScaleLinear<number, number>;
    if (convictionMetric === 'raw_implied') {
        yMetric = d3
            .scaleLinear()
            .domain([0, 1])
            .range([convBottom, convTop]);
    } else {
        const allValues = allConvictionPoints.map(getValue);
        const [vMin0, vMax0] = d3.extent(allValues) as [number, number];
        // Always include zero: a signed metric that never crosses it still reads
        // against it.
        const vMin = Math.min(vMin0 ?? 0, 0);
        const vMax = Math.max(vMax0 ?? 0, 0);
        const vRange = Math.max(Math.abs(vMax), Math.abs(vMin));
        const pad =
            convictionMetric === 'net_capital'
                ? Math.max(100, vRange * 0.12)
                : Math.max(1, vRange * 0.12);
        yMetric = d3
            .scaleLinear()
            .domain([vMin - pad, vMax + pad])
            .range([convBottom, convTop]);
    }

    const zeroY = convictionMetric !== 'raw_implied' ? yMetric(0) : null;
    const midY = convTop + (convBottom - convTop) / 2;
    const fallbackY = zeroY ?? midY;

    // ── Left axis ─────────────────────────────────────────────────────────
    if (convictionMetric === 'raw_implied') {
        // Shares drawPercentAxis for the formatting only — this is the group's
        // implied probability, not the market's.
        drawPercentAxis(svg, {
            ticks: [0, 0.25, 0.5, 0.75, 1],
            y: v => yMetric(v),
            x1: MARGIN.left,
            x2: MARGIN.left + w,
            labelSide: 'left',
            gridColor,
            textColor,
            fontSize: '8px',
        });
    } else {
        const metricTicks = yMetric.ticks(3).filter(d => d !== 0);
        svg.append('g')
            .selectAll('line')
            .data(metricTicks)
            .join('line')
            .attr('x1', MARGIN.left)
            .attr('y1', d => yMetric(d))
            .attr('x2', MARGIN.left + w)
            .attr('y2', d => yMetric(d))
            .attr('stroke', gridColor)
            .attr('stroke-width', 0.5);
        svg.append('g')
            .selectAll('text')
            .data(metricTicks)
            .join('text')
            .attr('x', MARGIN.left - 4)
            .attr('y', d => yMetric(d) + 3)
            .attr('fill', textColor)
            .attr('font-size', '8px')
            .attr('font-family', 'monospace')
            .attr('text-anchor', 'end')
            .text(d => metricTickLabel(d, convictionMetric));
    }

    // ── Lines ─────────────────────────────────────────────────────────────
    const convGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.conv})`);
    const zeroColorConv = isDark
        ? 'rgba(255,255,255,0.25)'
        : 'rgba(0,0,0,0.2)';

    // Area fills only make sense for a single group on a signed metric —
    // overlapping fills across groups would read as a stack.
    const showFill =
        convictionByGroup.size === 1 &&
        convictionMetric !== 'raw_implied' &&
        zeroY !== null;

    const greenArea = showFill
        ? d3
              .area<ConvictionTimePoint>()
              .x(d => xScale(d.ts))
              .y0(zeroY!)
              .y1(d => Math.min(yMetric(getValue(d)), zeroY!))
              .curve(d3.curveStepAfter)
        : null;
    const redArea = showFill
        ? d3
              .area<ConvictionTimePoint>()
              .x(d => xScale(d.ts))
              .y0(zeroY!)
              .y1(d => Math.max(yMetric(getValue(d)), zeroY!))
              .curve(d3.curveStepAfter)
        : null;

    const metricLine = d3
        .line<ConvictionTimePoint>()
        .x(d => xScale(d.ts))
        .y(d => yMetric(getValue(d)))
        .curve(d3.curveStepAfter);

    for (const [groupName, s] of convictionByGroup) {
        if (s.points.length === 0) continue;
        const color = CONV_GROUP_COLORS[groupName] ?? '#888';

        // Carry the last point forward so the line reaches the visible right edge.
        const lastPt = s.points[s.points.length - 1];
        const convExtendTo =
            eventEndDate && eventEndDate < now ? eventEndDate : now;
        const extendedPoints: ConvictionTimePoint[] =
            lastPt && lastPt.ts < convExtendTo
                ? [...s.points, { ...lastPt, ts: convExtendTo }]
                : s.points;

        if (greenArea) {
            convGroup
                .append('path')
                .datum(extendedPoints)
                .attr('d', greenArea)
                .attr('fill', alpha(theme.palette.success.main, 0.12));
        }
        if (redArea) {
            convGroup
                .append('path')
                .datum(extendedPoints)
                .attr('d', redArea)
                .attr('fill', alpha(theme.palette.error.main, 0.12));
        }

        convGroup
            .append('path')
            .datum(extendedPoints)
            .attr('d', metricLine)
            .attr('fill', 'none')
            .attr('stroke', color)
            .attr('stroke-width', 1.5)
            .attr('opacity', 0.85);

        // In raw mode, dots make the actual write cadence visible. Real points
        // only — never the carried-forward phantom.
        if (convictionRawMode && s.points.length > 0) {
            convGroup
                .append('g')
                .selectAll<SVGCircleElement, ConvictionTimePoint>('circle')
                .data(s.points)
                .join('circle')
                .attr('cx', d => xScale(d.ts))
                .attr('cy', d => yMetric(getValue(d)))
                .attr('r', 2)
                .attr('fill', color)
                .attr('opacity', 0.45);
        }
    }

    if (zeroY !== null) {
        svg.append('line')
            .attr('x1', MARGIN.left)
            .attr('x2', MARGIN.left + w)
            .attr('y1', zeroY)
            .attr('y2', zeroY)
            .attr('stroke', zeroColorConv)
            .attr('stroke-width', 1);
    }

    // ── Crosshair + tooltip ───────────────────────────────────────────────
    const bisectConvHover = d3.bisector<ConvictionTimePoint, Date>(
        d => d.ts
    ).left;

    const convDot = svg
        .append('circle')
        .attr('r', 3.5)
        .attr('fill', CONV_GROUP_COLORS['insider'] ?? '#f59e0b')
        .attr('stroke', isDark ? '#1a1a1a' : '#fff')
        .attr('stroke-width', 1.5)
        .style('display', 'none')
        .style('pointer-events', 'none');

    // Appended before the hover rect, then raised above it below, so the markers
    // win the pointer while the rect still covers the whole panel.
    const convSigGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.convSig})`)
        .attr('class', 'conv-signal-overlay');

    svg.append('rect')
        .attr('x', MARGIN.left)
        .attr('y', convTop)
        .attr('width', w)
        .attr('height', convHeight)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event: MouseEvent) => {
            // Same guard as the probability panel: markers own their own hover.
            if (
                (event.target as SVGElement).closest?.('.conv-signal-overlay')
            )
                return;

            const [mx] = d3.pointer(event, svgEl);
            const hoverDate = xScale.invert(mx);

            // Snap across all groups, preferring 'insider'.
            const primarySeries =
                convictionByGroup.get('insider') ??
                [...convictionByGroup.values()][0];
            if (!primarySeries || primarySeries.points.length === 0) return;

            const pts = primarySeries.points;
            const idx = bisectConvHover(pts, hoverDate, 1);
            const p0 = pts[idx - 1];
            const p1 = pts[idx];
            const snapped =
                !p1 ||
                hoverDate.getTime() - p0.ts.getTime() <
                    p1.ts.getTime() - hoverDate.getTime()
                    ? p0
                    : p1;
            if (!snapped) return;

            const groupColor =
                CONV_GROUP_COLORS[primarySeries.group] ?? '#f59e0b';

            chrome.showCross(mx);
            convDot
                .style('display', null)
                .attr('fill', groupColor)
                .attr('cx', xScale(snapped.ts))
                .attr('cy', yMetric(getValue(snapped)));

            placeTooltip(
                tooltipRef.current,
                event,
                buildConvictionTooltipHtml(
                    snapped,
                    primarySeries.group,
                    groupColor,
                    convictionRawMode,
                    tc
                )
            );
        })
        .on('mouseleave', () => {
            chrome.hideCross();
            convDot.style('display', 'none');
            chrome.hideTooltip();
        });

    convSigGroup.raise();

    // ── Signal markers ────────────────────────────────────────────────────
    const visibleSigs = (traderSignals ?? []).filter(sig => {
        const t = new Date(sig.opened_at);
        return t >= xDomain[0] && t <= xDomain[1];
    });
    if (visibleSigs.length === 0) return;

    const primarySeries =
        convictionByGroup.get('insider') ?? [...convictionByGroup.values()][0];
    const convBisect = primarySeries
        ? d3.bisector<ConvictionTimePoint, Date>(d => d.ts).left
        : null;

    const getConvCapY = (date: Date): number => {
        if (!primarySeries || !convBisect) return fallbackY;
        const pts = primarySeries.points;
        const idx = convBisect(pts, date, 1);
        const pt = pts[idx - 1] ?? pts[0];
        return pt ? yMetric(getValue(pt)) : fallbackY;
    };

    for (const sig of visibleSigs) {
        const t = new Date(sig.opened_at);
        const cx = xScale(t);
        const cy = getConvCapY(t);
        const groupColor = CONV_GROUP_COLORS[sig.trader_group] ?? '#888';
        const size = 5;
        const isClosed = sig.status === 'closed';
        const pathD = markerPath(cx, cy, size, sig.direction);

        // The visible mark takes no pointer events; a larger invisible circle
        // handles them, so the background crosshair rect cannot steal the hover.
        const visPath = convSigGroup
            .append('path')
            .datum(sig)
            .attr('d', pathD)
            .attr('fill', isClosed ? 'none' : alpha(groupColor, 0.7))
            .attr('stroke', groupColor)
            .attr('stroke-width', 1.5)
            .attr('opacity', isClosed ? 0.5 : 0.9)
            .style('pointer-events', 'none');

        convSigGroup
            .append('circle')
            .attr('data-conv-sig', '1')
            .datum<SignalItem>(sig)
            .attr('cx', cx)
            .attr('cy', cy)
            .attr('r', 9)
            .attr('fill', 'transparent')
            .attr('stroke', 'none')
            .style('cursor', 'pointer')
            .on('mouseenter', function (event: MouseEvent, d) {
                chrome.hideCross();
                convDot.style('display', 'none');
                chrome.hideTooltip();
                visPath
                    .attr('d', markerPath(cx, cy, size * 1.5, sig.direction))
                    .attr('stroke-width', 2)
                    .attr('opacity', 1);
                placeTooltip(
                    tooltipRef.current,
                    event,
                    buildMarkerTooltipHtml(
                        preparedFromTraderSignal(d, cx, cy),
                        questionMap as Map<string, string>,
                        tc
                    )
                );
                onTraderHover?.(d.address ?? '');
            })
            .on('mousemove', function (event: MouseEvent) {
                placeTooltip(
                    tooltipRef.current,
                    event,
                    tooltipRef.current?.innerHTML ?? ''
                );
            })
            .on('mouseleave', function () {
                visPath
                    .attr('d', pathD)
                    .attr('stroke-width', 1.5)
                    .attr('opacity', isClosed ? 0.5 : 0.9);
                chrome.hideTooltip();
                onTraderHover?.(null);
            });
    }
}
